import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import CommonModal from "components/common/CommonModal";
import EnhancedTable from "components/common/EnhancedTable";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import type { Editor } from "grapesjs";

import MarksheetTemplateBuilderEditor from "components/marksheet-template-builder/MarksheetTemplateBuilderEditor";
import {
  buildPreviewRenderData,
  renderMarksheetTemplate,
} from "components/marksheet-template-builder/renderTemplate";
import {
  getDemoProjectData,
  getBlankProjectData,
} from "components/marksheet-template-builder/templates";
import { openPrintPreviewInNewTab } from "components/marksheet-template-builder/preview";
import type { MarksheetTemplateProjectData } from "services/marksheetTemplateBuilderService";
import {
  listMarksheetTemplatesFromApi,
  createMarksheetTemplate,
  updateMarksheetTemplate,
  setPrimaryMarksheetTemplate,
  deleteMarksheetTemplate,
  getMarksheetTemplateFiles,
  springDateToTs,
  type MarksheetTemplateApiRecord,
} from "services/marksheetTemplateApiService";
import { useSessionClass } from "contexts/SessionClassContext";
import { useAuth } from "contexts/AuthContext";
import { useError } from "contexts/ErrorContext";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(ts: number) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function readDraft(key: string): MarksheetTemplateProjectData | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { _draftSavedAt?: number } & MarksheetTemplateProjectData;
    if (parsed._draftSavedAt && Date.now() - parsed._draftSavedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// ── Editor mode ───────────────────────────────────────────────────────────────

type EditorModeNew = { type: "new"; name: string; starter: "demo" | "blank" };
type EditorModeEdit = { type: "edit"; record: MarksheetTemplateApiRecord };
type EditorMode = EditorModeNew | EditorModeEdit | null;

// ── Component ─────────────────────────────────────────────────────────────────

export default function MarksheetTemplateBuilderPage() {
  const { selectedClass, selectedSession } = useSessionClass();
  const { user } = useAuth();
  const { showSuccess, showError, showWarning, showInfo } = useError();

  const classId = selectedClass?.id ?? null;
  const sessionId = selectedSession?.id ?? null;
  const className = selectedClass?.name ?? "";
  const sessionName = selectedSession?.name ?? "";
  const userId = user?.id ?? 1;

  // ── Template list ─────────────────────────────────────────────────────────

  const [templates, setTemplates] = useState<MarksheetTemplateApiRecord[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const loadTemplates = useCallback(async () => {
    if (!className || !sessionId) {
      setTemplates([]);
      return;
    }
    setListLoading(true);
    setListError("");
    try {
      const data = await listMarksheetTemplatesFromApi(className, sessionId);
      setTemplates(data);
    } catch (err: any) {
      setListError(err?.response?.data?.message || "Failed to load templates from server.");
    } finally {
      setListLoading(false);
    }
  }, [className, sessionId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // ── Editor state ──────────────────────────────────────────────────────────

  const [editorMode, setEditorMode] = useState<EditorMode>(null);
  const [editorInitData, setEditorInitData] = useState<MarksheetTemplateProjectData | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [hasDraftRestored, setHasDraftRestored] = useState(false);
  const editorRef = useRef<Editor | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Draft key — stable ref so handleAutoSave doesn't need to re-create on each render
  const draftKeyRef = useRef<string | null>(null);
  const draftKey = editorMode
    ? editorMode.type === "new"
      ? `ms_draft_new_${className}_${sessionId}_${editorMode.name}`
      : `ms_draft_edit_${editorMode.record.id}`
    : null;
  draftKeyRef.current = draftKey;

  const handleAutoSave = useCallback((data: MarksheetTemplateProjectData) => {
    const key = draftKeyRef.current;
    if (!key) return;
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          ...data,
          _msCss: editorRef.current?.getCss() ?? (data as any)._msCss ?? "",
          _msHtml: editorRef.current?.getHtml() ?? (data as any)._msHtml ?? "",
          _draftSavedAt: Date.now(),
        })
      );
    } catch {}
  }, []);

  // ── Create dialog ─────────────────────────────────────────────────────────

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [starter, setStarter] = useState<"demo" | "blank">("demo");
  const [createError, setCreateError] = useState("");

  const openCreate = () => {
    setNewName("");
    setStarter("demo");
    setCreateError("");
    setCreateOpen(true);
  };

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) { setCreateError("Template name is required."); return; }

    const newDraftKey = `ms_draft_new_${className}_${sessionId}_${name}`;
    const draft = readDraft(newDraftKey);
    const initData = draft ?? (starter === "demo" ? getDemoProjectData() : getBlankProjectData());

    setEditorInitData(initData);
    setHasDraftRestored(!!draft);
    setEditorMode({ type: "new", name, starter });
    setEditorKey((k) => k + 1);
    setCreateOpen(false);
  };

  // ── Edit existing template ────────────────────────────────────────────────

  const handleEditTemplate = async (record: MarksheetTemplateApiRecord) => {
    const editDraftKey = `ms_draft_edit_${record.id}`;
    const draft = readDraft(editDraftKey);

    if (draft) {
      setEditorInitData(draft);
      setHasDraftRestored(true);
      setEditorMode({ type: "edit", record });
      setEditorKey((k) => k + 1);
      return;
    }

    setEditorLoading(true);
    try {
      const { htmlContent, cssContent } = await getMarksheetTemplateFiles(
        record.htmlFile,
        record.cssFile
      );
      setEditorInitData({
        pages: [{ name: "Marksheet", component: htmlContent }],
        styles: [],
        _msCss: cssContent,
        _msHtml: htmlContent,
      });
      setHasDraftRestored(false);
      setEditorMode({ type: "edit", record });
      setEditorKey((k) => k + 1);
    } catch {
      showError("Failed to load template files. Please try again.");
    } finally {
      setEditorLoading(false);
    }
  };

  // ── Save (create or update) ───────────────────────────────────────────────

  const handleSave = async () => {
    if (!editorMode) return;
    if (!classId || !sessionId || !className || !sessionName) {
      showWarning("Please select the Class and Session in the header first.");
      return;
    }
    const ed = editorRef.current;
    if (!ed) return;
    const htmlContent = ed.getHtml();
    const cssContent = ed.getCss() ?? "";
    if (!htmlContent) {
      showWarning("Template is empty. Design something first.");
      return;
    }
    setSaving(true);
    try {
      if (editorMode.type === "new") {
        const record = await createMarksheetTemplate({
          classId,
          className,
          sessionId,
          sessionName,
          createdBy: userId,
          updatedBy: userId,
          templateName: editorMode.name,
          primary: false,
          htmlContent,
          cssContent,
        });
        localStorage.removeItem(`ms_draft_new_${className}_${sessionId}_${editorMode.name}`);
        showSuccess(`Template "${record.templateName}" created successfully.`);
      } else {
        const { record } = editorMode;
        const updated = await updateMarksheetTemplate({
          id: record.id,
          classId: record.classId,
          className: record.className,
          sessionId: record.sessionId,
          sessionName: record.sessionName,
          createdBy: record.createdBy,
          updatedBy: userId,
          templateName: record.templateName,
          primary: record.primary,
          htmlContent,
          cssContent,
        });
        localStorage.removeItem(`ms_draft_edit_${record.id}`);
        showSuccess(`Template "${updated.templateName}" updated successfully.`);
      }
      handleBack();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Save failed.";
      showError(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Set / Unset primary ───────────────────────────────────────────────────

  const [primaryLoading, setPrimaryLoading] = useState<number | null>(null);

  const handleSetPrimary = async (record: MarksheetTemplateApiRecord) => {
    setPrimaryLoading(record.id);
    try {
      if (record.primary) {
        // Unset primary: fetch files then update with primary:false
        const { htmlContent, cssContent } = await getMarksheetTemplateFiles(
          record.htmlFile,
          record.cssFile
        );
        await updateMarksheetTemplate({
          id: record.id,
          classId: record.classId,
          className: record.className,
          sessionId: record.sessionId,
          sessionName: record.sessionName,
          createdBy: record.createdBy,
          updatedBy: userId,
          templateName: record.templateName,
          primary: false,
          htmlContent,
          cssContent,
        });
        await loadTemplates();
        showInfo(`"${record.templateName}" is no longer primary. Default view will be shown now.`);
      } else {
        await setPrimaryMarksheetTemplate(record.id);
        await loadTemplates();
        showSuccess(`"${record.templateName}" set as primary template.`);
      }
    } catch (err: any) {
      showError(err?.response?.data?.message || "Failed to update primary status.");
    } finally {
      setPrimaryLoading(null);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const [deleteTarget, setDeleteTarget] = useState<MarksheetTemplateApiRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteMarksheetTemplate(deleteTarget.id);
      await loadTemplates();
      setDeleteTarget(null);
      showSuccess("Template deleted successfully.");
    } catch (err: any) {
      showError(err?.response?.data?.message || "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  // ── Editor callbacks ──────────────────────────────────────────────────────

  const onEditorReady = (ed: Editor) => { editorRef.current = ed; };

  const handlePreview = () => {
    const ed = editorRef.current;
    if (!ed) return;
    const { html: rendered, css: renderedCss } = renderMarksheetTemplate(
      ed.getHtml(),
      ed.getCss() ?? "",
      buildPreviewRenderData()
    );
    openPrintPreviewInNewTab({ title: "Template Preview (sample data)", html: rendered, css: renderedCss });
  };

  const handleReset = async () => {
    if (draftKey) localStorage.removeItem(draftKey);
    setHasDraftRestored(false);
    editorRef.current = null;

    if (editorMode?.type === "new") {
      // Restore the original starter the user chose (demo or blank)
      setEditorInitData(
        editorMode.starter === "blank" ? getBlankProjectData() : getDemoProjectData()
      );
      setEditorKey((k) => k + 1);
    } else if (editorMode?.type === "edit") {
      // Reload the last-saved version from server
      setEditorLoading(true);
      try {
        const { htmlContent, cssContent } = await getMarksheetTemplateFiles(
          editorMode.record.htmlFile,
          editorMode.record.cssFile
        );
        setEditorInitData({
          pages: [{ name: "Marksheet", component: htmlContent }],
          styles: [],
          _msCss: cssContent,
          _msHtml: htmlContent,
        });
      } catch {
        showError("Failed to reload template from server.");
      } finally {
        setEditorLoading(false);
      }
      setEditorKey((k) => k + 1);
    }
  };

  const handleBack = () => {
    editorRef.current = null;
    setEditorMode(null);
    setHasDraftRestored(false);
    loadTemplates();
  };

  // ── Render: editor mode ───────────────────────────────────────────────────

  if (editorMode) {
    const templateName =
      editorMode.type === "new" ? editorMode.name : editorMode.record.templateName;
    const isSaved = editorMode.type === "edit";
    const editorKeyStr = `${editorKey}`;

    return (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.08)",
            background: "linear-gradient(135deg,#fff 0%,#f8fafe 100%)",
            mb: 1,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <Button variant="text" startIcon={<ArrowBackIcon />} onClick={handleBack}>
              Templates
            </Button>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                  {templateName}
                </Typography>
                {hasDraftRestored && (
                  <Chip
                    label="Draft restored"
                    size="small"
                    color="warning"
                    variant="outlined"
                    onDelete={() => setHasDraftRestored(false)}
                    sx={{ height: 20, fontSize: 11 }}
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {isSaved
                  ? `Server ID: ${editorMode.record.id} · ${editorMode.record.className} · ${editorMode.record.sessionName}`
                  : `New template · ${className || "No class selected"}`}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<RemoveRedEyeIcon />}
                onClick={handlePreview}
                size="small"
              >
                Preview
              </Button>
              <Button
                variant="contained"
                startIcon={
                  saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />
                }
                onClick={handleSave}
                disabled={saving || !classId || !sessionId}
                size="small"
                color={isSaved ? "success" : "primary"}
              >
                {saving ? "Saving…" : isSaved ? "Update" : "Save to Server"}
              </Button>
              <Button
                variant="text"
                color="inherit"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                size="small"
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {(!classId || !sessionId) && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Select the Class and Session in the header — required to save to server.
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.08)",
            overflow: "hidden",
            height: "calc(100vh - 160px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
            <MarksheetTemplateBuilderEditor
              key={editorKeyStr}
              editorKey={editorKeyStr}
              initialData={editorInitData}
              onEditorReady={onEditorReady}
              onAutoSave={handleAutoSave}
            />
          </Box>
        </Paper>

      </Box>
    );
  }

  // ── Render: list mode ─────────────────────────────────────────────────────

  return (
    <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.25rem", whiteSpace: "nowrap" }}>
            Marksheet Template Builder
          </Typography>
          {/* {(classId && sessionId) && (
            <Typography variant="body2" color="text.secondary">
              {`Templates for: ${className} · ${sessionName}`}
            </Typography>
          )} */}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={openCreate}
            disabled={!classId || !sessionId}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
          >
            New Template
          </Button>
        </Box>
      </Box>

      {(!classId || !sessionId) && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please select a Class and Session from the header to manage templates.
        </Alert>
      )}

      {listError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadTemplates}>
                Retry
              </Button>
            }
          >
            {listError}
          </Alert>
        ) : (
          <EnhancedTable
            columns={[
              { id: "templateName", label: "Template Name", minWidth: 150 },
              { id: "className", label: "Class", minWidth: 100 },
              { id: "sessionName", label: "Session", minWidth: 100 },
              {
                id: "primary",
                label: "Primary",
                minWidth: 80,
                format: (value: boolean) =>
                  value ? (
                    <Chip
                      label="Primary"
                      size="small"
                      color="success"
                      icon={<StarIcon sx={{ fontSize: "14px !important" }} />}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  ),
              },
              {
                id: "createdAt",
                label: "Created",
                minWidth: 100,
                format: (value: any) => formatDate(springDateToTs(value)),
              },
            ]}
            data={templates}
            loading={listLoading}
            searchFields={["templateName", "className", "sessionName"]}
            hideSearch
            externalSearchTerm={searchTerm}
            actions={(row: MarksheetTemplateApiRecord) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="Edit template">
                  <IconButton
                    size="small"
                    onClick={() => handleEditTemplate(row)}
                    disabled={editorLoading}
                  >
                    {editorLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <EditIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={row.primary ? "Remove primary (restore default view)" : "Set as primary template"}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleSetPrimary(row)}
                    disabled={primaryLoading !== null}
                  >
                    {primaryLoading === row.id ? (
                      <CircularProgress size={16} />
                    ) : row.primary ? (
                      <StarIcon fontSize="small" sx={{ color: "#f59e0b" }} />
                    ) : (
                      <StarBorderIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
            cardProps={{
              elevation: 0,
              sx: {
                border: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
                mb: 0,
                "& .MuiCardContent-root": {
                  p: 0,
                  "&:last-child": { pb: 0 },
                },
                "& .MuiCardContent-root > .MuiBox-root:first-of-type": {
                  display: "none",
                },
              },
            }}
          />
        )}

      {/* ── Create dialog ── */}
      <CommonModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Template"
        maxWidth="xs"
        fullWidth
        onSubmit={handleCreate}
        submitLabel="Open Editor"
        cancelLabel="Cancel"
      >
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {createError && <Alert severity="error">{createError}</Alert>}
          <TextField
            label="Template Name"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setCreateError(""); }}
            fullWidth
            autoFocus
            required
            size="small"
          />
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Start with
            </Typography>
            <RadioGroup
              value={starter}
              onChange={(e) => setStarter(e.target.value as "demo" | "blank")}
            >
              <FormControlLabel
                value="demo"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Demo template</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Complete A4 layout with all tokens already placed.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="blank"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Blank canvas</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Build by dragging blocks and tokens.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>
        </Stack>
      </CommonModal>

      {/* ── Delete confirm dialog ── */}
      <CommonModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete Template"
        maxWidth="xs"
        fullWidth
        customActions={
          <>
            <Button
              onClick={() => setDeleteTarget(null)}
              variant="outlined"
              disabled={deleting}
              sx={{
                borderColor: "#3092e3",
                color: "#3092e3",
                "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" },
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
              {deleting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Delete
            </Button>
          </>
        }
      >
        <Typography>
          Delete <strong>{deleteTarget?.templateName}</strong>? This will permanently remove it
          from the server.
        </Typography>
      </CommonModal>

    </Paper>
  );
}
